/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ForgotPassword from './ForgotPassword.tsx';
import EmailVerificationHandler from './EmailVerificationHandler.tsx';
import LoginForm from './LoginForm.tsx';
import LoadingSpinner from '../LoadingSpinner.tsx';
import { auth, db } from '../../firebase.js';
import { checkConstructionMode } from '../../utils/constructionMode.ts';

const LoginComponent = ({onDataSend}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [welcomeActive, setWelcomeActive] = useState(false);
  // Dev helper: ?preview=welcome to bypass login
  const urlParams = new URLSearchParams(window.location.search);
  const previewWelcome = urlParams.get('preview') === 'welcome';
  // Wait for Firebase auth to initialize
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('Auth state changed:', user ? 'User logged in' : 'No user');
      setAuthReady(true);
    });
  
    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    console.log('Login attempt started');
    setMessage('Logging In');
    setIsLoading(true);
  console.log('previewWelcome ',previewWelcome)
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Get ID token with custom claims
      const idTokenResult = await userCredential.user.getIdTokenResult();
      const idToken = idTokenResult.token;
      const userId = userCredential.user.uid;
      const userEmail = userCredential.user.email;
      
      // IMPORTANT: Get isAdmin from custom claims, not Firestore
      const isAdmin = idTokenResult.claims.isAdmin || false;
      
      console.log('ID Token exists:', !!idToken);
      console.log('Token preview:', idToken?.substring(0, 20) + '...');
      console.log('Admin status from custom claim:', isAdmin);

      // CHECK CONSTRUCTION MODE BEFORE PROCEEDING
      const constructionCheck = await checkConstructionMode(isAdmin);
      if (!constructionCheck.allowed) {
        setMessage(constructionCheck.message || 'Access denied');
        setIsLoading(false);
        return; // Exit login process
      }
      
      setMessage('Gathering Data');
      console.log('Calling unified Netlify function...');

      // Get user data from Firestore FIRST (fast!)
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);

     // console.log('User document exists:', userDoc.exists());
      
      const userData = userDoc.data();
     // console.log('Full userData loaded:', userData);
      
      // Call Firebase Function to sync email verification
      try {
        const functions = getFunctions();
        const syncEmailVerification = httpsCallable(functions, 'syncEmailVerificationOnSignIn');
        
       // console.log('🔒 Calling Firebase Function with App Check...');
        await syncEmailVerification();
       // console.log('✅ Firebase Function completed');
        
        // Wait for Firestore to propagate the lastSignInAt update
      //  console.log('⏳ Waiting for Firestore to sync...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
      } catch (firebaseError) {
        console.error('❌ Firebase Function error:', firebaseError);
      }
     
      // Helper to create data object
      const createDataObject = (additionalData = {}) => ({
        success: true,
        data: {
          userId: userId,
          email: userEmail,
          firstname: userData?.firstname || '',
          lastname: userData?.lastname || '',
          isAdmin: isAdmin,
          tags: userData?.collectionHandles || [],
          salesDataByTag: [],
          tagsArray: userData?.collectionHandles || [],
          ...additionalData
        }
      });

      // Start Shopify/Printful fetch
      const salesDataPromise = fetch(`${API_BASE_URL}/.netlify/functions/get-user-sales-data`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
      }).then(async (response) => {
        console.log('Response status:', response.status);
        
        try {
          const data = await response.json();
          
          // Handle 500 errors gracefully
          if (response.status === 500) {
            console.warn('500 error received:', data);
            return createDataObject();
          }
          
          if (!response.ok) {
            console.error('Non-OK response:', response.status, data);
            return createDataObject();
          }
          
          // Success!
          return data;
          
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          return createDataObject();
        }
      }).catch((error) => {
        console.error('Shopify/Printful API error:', error);
        return createDataObject();
      });

      // Wait for sales data, then send ONCE
      const finalData = await salesDataPromise;
      
      // ✅ Send data only ONCE with all info
      onDataSend(finalData, idToken);
      //console.log('Sales data sent:', finalData);
        
      setMessage(`Welcome Back ${userData?.firstname || 'User'}`);
        
    } catch (error) {
      console.error('Login error:', error);
      setMessage(`Login failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      {/* Email Verification Handler - shows welcome screen when needed */}
      <EmailVerificationHandler 
        authReady={authReady}
        onComplete={() => {
          console.log('User completed email verification flow');
        }}
         onWelcomeVisible={(visible) => setWelcomeActive(visible)}
      />

      {/* Main login UI */}
      <div id="user-ready" style={{ display: welcomeActive ? 'none' : 'block' }}>
        <h1 className="mouse">Merch Sherpa <span>$</span>ales Portal</h1>
        {
          isLoading && (
            <div className="loading-block">
              <b>LOADING</b><br/>
              <LoadingSpinner size={40} />
            </div>
          )
        }
        <div>
          {!isForgotPassword && !isLoading && (
            <LoginForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              handleLogin={handleLogin}
              isLoading={isLoading}
              message={message}
            />
          )}
        </div>
        <div>
          {isForgotPassword && <ForgotPassword onBackToLogin={() => setIsForgotPassword(false)} />}
          {!isForgotPassword && !isLoading && <button className="btn btn--small" onClick={() => setIsForgotPassword(true)}>Forgot Password</button>}
        </div>
      </div> 
    </div>
  );
};

export default LoginComponent;